import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { ArrowUpCircle, CircleAlert } from 'lucide-react';
import { Button, Dialog, Field, Select } from '@/components/ui';
import { idempotencyKey } from '@/utils/idempotency';
import { useSiteOperation } from '@/hooks/useSites';
import { useActiveTemplates } from '@/hooks/useTemplates';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import type { SiteDetail } from '@/types/api';

/**
 * "Обновить WordPress/PHP" (S4): the site moves to another template version,
 * or stays on its own to get the current runtime. What happens is written out,
 * because the operator agrees to a minute or two of maintenance.
 */
export function UpgradeSiteDialog({
  site,
  open,
  onClose,
}: {
  readonly site: SiteDetail;
  readonly open: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const [chosen, setChosen] = useState('');
  // One key per opened dialog: a double click or a retry is the same update.
  const key = useRef(idempotencyKey('site.upgrade'));

  const templates = useActiveTemplates({ enabled: open });
  const versions = templates.data ?? [];
  const target = chosen || site.templateVersionId || versions[0]?.id || '';
  const same = target === site.templateVersionId;

  const upgrade = useSiteOperation(site.id, {
    onSuccess: () => {
      toast.success('Обновление поставлено в очередь');
      key.current = idempotencyKey('site.upgrade');
      setChosen('');
      onClose();
    },
  });

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (!target || upgrade.isPending) return;
    upgrade.mutate({ command: { operation: 'upgrade', templateVersionId: target }, key: key.current });
  };

  return (
    <Dialog
      open={open}
      title="Обновить WordPress/PHP"
      description={site.templateName ? `Сейчас: ${site.templateName} ${site.templateVersion ?? ''}` : 'Шаблон сайта не указан'}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={upgrade.isPending}>
            Отмена
          </Button>
          <Button type="submit" form="upgrade-site" variant="primary" icon={ArrowUpCircle} busy={upgrade.isPending} disabled={!target}>
            {same ? 'Обновить среду' : 'Обновить'}
          </Button>
        </>
      }
    >
      <form id="upgrade-site" className="stack" onSubmit={submit} noValidate>
        <Field label="Версия шаблона" htmlFor="upgrade-template" hint="Версия WordPress и PHP задаётся шаблоном">
          <Select
            id="upgrade-template"
            label="Версия шаблона"
            value={target}
            onChange={setChosen}
            disabled={versions.length === 0}
            placeholder={templates.isPending ? 'Загрузка…' : 'Нет активных шаблонов'}
            options={versions.map((version) => ({
              value: version.id,
              label: `${version.name} ${version.version}`,
              hint: version.id === site.templateVersionId ? 'текущая' : version.imageDigest.slice(0, 19),
            }))}
          />
        </Field>

        <ol className="enroll-steps">
          <li>Создаётся резервная копия, сайт переводится в режим обслуживания.</li>
          <li>Ядро WordPress обновляется из образа шаблона, база данных — под новую версию. Файлы в корне сайта сохраняются.</li>
          <li>Сайт проверяется и открывается.</li>
        </ol>
        <p className="field-hint">
          {same ? 'Сайт получит актуальную конфигурацию: лимиты PHP, почту, системный планировщик.' : 'При сбое сайт вернётся к прежней версии.'}{' '}
          Занимает 1–3 минуты; сессии администраторов могут быть сброшены.
        </p>

        {upgrade.isError ? (
          <p className="field-error" role="alert">
            <CircleAlert size={14} aria-hidden="true" />
            {errorText(upgrade.error, 'Не удалось поставить обновление в очередь')}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
